import { useCallback, useEffect, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import { categoriesApi } from '@/api';
import { ProductCatalogNav } from '@/components/org/ProductCatalogNav';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { useDebounce } from '@/hooks/useDebounce';
import { usePagination } from '@/hooks/usePagination';
import type { CategoryRequest, CategoryResponse } from '@/types/product';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';

const empty: CategoryRequest = { name: '' };

export function CategoriesPage() {
  const pagination = usePagination();
  const debouncedSearch = useDebounce(pagination.search);
  const [data, setData] = useState<CategoryResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<CategoryResponse | null>(null);
  const [form, setForm] = useState<CategoryRequest>(empty);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await categoriesApi.list({ page: pagination.page, size: pagination.size, search: debouncedSearch || undefined });
      setData(res.content);
      pagination.applyPageResponse(res);
    } catch (err) { showApiError(err, 'Failed to load'); }
    finally { setLoading(false); }
  }, [pagination.page, pagination.size, debouncedSearch, pagination]);

  useEffect(() => { load(); }, [load]);

  const columns: Column<CategoryResponse>[] = [
    { key: 'name', header: 'Name' },
    { key: 'sortOrder', header: 'Order' },
    { key: 'status', header: 'Status', render: (r) => (r.status ? 'Active' : 'Inactive') },
    { key: 'edit', header: '', render: (r) => (
      <button type="button" className="text-primary-600" onClick={() => { setEditing(r); setForm({ name: r.name, parentId: r.parentId, sortOrder: r.sortOrder }); setModalOpen(true); }}>Edit</button>
    )},
  ];

  const save = async () => {
    setSaving(true);
    try {
      if (editing) await categoriesApi.update(editing.id, form);
      else await categoriesApi.create(form);
      notify.success('Saved');
      setModalOpen(false);
      load();
    } catch (err) { showApiError(err, 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <PageHeader
        title="Categories"
        breadcrumbs={[
          { label: 'POS', to: '/pos/dashboard' },
          { label: 'Products', to: '/pos/products' },
          { label: 'Categories' },
        ]}
        actions={
          <Button onClick={() => { setEditing(null); setForm(empty); setModalOpen(true); }}>
            <AddIcon fontSize="small" /> Add category
          </Button>
        }
      />
      <ProductCatalogNav />
      <DataTable columns={columns} data={data} keyField="id" loading={loading} search={pagination.search}
        onSearchChange={(v) => { pagination.setSearch(v); pagination.resetPage(); }}
        page={pagination.page} totalPages={pagination.totalPages} onPageChange={pagination.setPage} />
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editing ? 'Edit' : 'New Category'}
        footer={<><Button variant="secondary" onClick={() => setModalOpen(false)}>Cancel</Button><Button onClick={save} loading={saving}>Save</Button></>}>
        <Input label="Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
      </Modal>
    </div>
  );
}
